import {DsAvatar, PhoneHQGlyph, NavRailItem, NavRailLogo, PhoneHQDropdown, PhoneHQTooltip} from 'ch3-ui-lib';
import {LOGOUT_REQUEST_EVENT, LogoutDialog} from "./LogoutDialog";
import {TelcoStatus} from "../../store/features/Team/teamService";
import {House} from "lucide-react";
import {isSettingsPath} from "../../pages/Settings/settingsNav";
import type {ReactNode} from "react";
import {activityStyles, sidebarAvatarStyles, sidebarStyles} from "./helpers";
import {clsxMerge} from "../../utilis/classNameUtils";
import {Link} from "react-router-dom";
import {useKeycloak} from "@react-keycloak/web";
import {useEffect, useMemo, useRef, useState} from "react";
import useMediaQuery from "../../utilis/screenWidthUtils.ts";
import {SetStatusDialog} from "./SetStatusDialog";
import {hexToEmoji} from "../Emoji/emoji";
import {convertFromISOFormat} from "../../utilis/timeUtilis";
import {useNavigate} from "react-router-dom";
import {useFetchCurrentUserQuery, User, useToggleDndMutation} from "../../store/features/Users/userService";
import {useDispatch, useSelector} from "react-redux";
import {teamApi, useGetStatusesQuery} from "../../store/features/Team/teamService";
import {mergeStatuses} from "../../store/features/Team/statusesLongPoll";
import {useStatusesLongPoll} from "../../hooks/useStatusesLongPoll";
import {selectIsSuperadmin, selectTenant, selectUserId, selectUserRole} from "../../store/features/Auth/authSlice";
import { useLocation } from 'react-router-dom';
import {SidebarSubmenu} from "./SidebarSubmenu.tsx";
import Gleap from "gleap";
import {navigateDialer} from "../../store/features/calls/dialerSlice.ts";
import {getUserStatus, getUserStatusName} from "../../utilis/getStatus.ts";
import googleCalendarIcon from './../../assets/google-calendar.svg'
import {DndDialog} from "../Dnd/DndDialog.tsx";
import {dndShortValue, isDndOn, parseDue, toDndPayload} from "../Dnd/dndTime.ts";
import {Bell, ChevronRight, LogOut, Moon, Settings, Smile, UserRound} from "lucide-react";
import {RootState} from "../../store/store.ts";
import {performLogout} from "../../services/performLogout.ts";
import {checkMeetingStatusExpiration, checkStatusExpiration} from "../../utilis/statusUtilis.ts";
import {useMatrixContext} from "../../providers/MatrixProvider.tsx";


export interface ChSidebarItem {
    icon?: any;
    /** Lucide icon shown on the workspace navigation rail. */
    railIcon?: ReactNode;
    label: string;
    link: string;
    badge?: string;
    subItems?: ChSidebarItem[];
    labelId?: string;
}

export interface SectionItems {
    title?: string;
    items: ChSidebarItem[]
}

export interface ChSidebarProps {
    sections: SectionItems[];
    bottomMenu?: ChSidebarItem[];
    logo?: JSX.Element;
    isEnsEnabled?: boolean;
}

// Presence dot on the nav-rail avatar (design NavRail): Talking reads as busy.
const navStatusDot: Partial<Record<TelcoStatus, string>> = {
    [TelcoStatus.ONLINE]: 'bg-success',
    [TelcoStatus.TALKING]: 'bg-status-busy',
    [TelcoStatus.DND]: 'bg-status-dnd',
    [TelcoStatus.OFFLINE]: 'bg-status-offline',
    [TelcoStatus.UNKNOWN]: 'bg-status-offline',
};

// Platform support only works in the admin pages, which live in the Settings frame.
const superadminSections: SectionItems[] = [{
    items: [{
        label: 'Administration',
        link: '/admin/report',
        railIcon: <Settings size={20} aria-hidden="true" className="!p-0 !text-current"/>,
    }],
}];

// The rail item that owns the Settings frame (Settings, or Administration for platform support).
const SETTINGS_RAIL_LINKS = ['/settings', '/admin/report'];

export default function ChSidebar({sections: workspaceSections, logo, bottomMenu, isEnsEnabled = false}: ChSidebarProps) {
    const [isLogoutOpen, setIsLogoutOpen] = useState(false);

    const [isActiveItem, setIsActiveItem] = useState<ChSidebarItem | undefined>(undefined);
    const [isHomeActive, setIsHomeActive] = useState(false);
    const [preventTooltipOpen, SetPreventTooltipOpen] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const location = useLocation();
    const {pathname} = location;

    const role = useSelector(selectUserRole);
    const callSate = useSelector((state: RootState) => state.dialer.callState);

    const isNavigatingRef = useRef(false);
    const dispatch = useDispatch();

    const {logout: matrixLogout, unreadBadgeCount} = useMatrixContext();


    const navigate = useNavigate();
    const [toggleDnd] = useToggleDndMutation();

    const userId = useSelector(selectUserId)
    const tenant = useSelector(selectTenant)
    const isSuperadmin = useSelector(selectIsSuperadmin)
    const sections = isSuperadmin ? superadminSections : workspaceSections;

    const {data: user = {} as User} = useFetchCurrentUserQuery(userId, {skip: !userId || isSuperadmin});

    const userFullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

    // The sidebar's own status must stay independent from Chat's bulk status subscription.
    const ownStatusQuery = useMemo(() => ({users: user?.userId ? [user.userId] : []}), [user?.userId]);
    const skipOwnStatus = !user?.userId || isSuperadmin;
    const {currentUserStatus, refetch: refetchStatuses} = useGetStatusesQuery(
        ownStatusQuery,
        {
            skip: skipOwnStatus,
            refetchOnMountOrArgChange: true,
            selectFromResult: ({data, ...rest}) => ({
                ...rest,
                currentUserStatus: data?.[0]
            })
        }
    );
    useStatusesLongPoll({
        users: ownStatusQuery.users,
        update: (incoming) => teamApi.util.updateQueryData('getStatuses', ownStatusQuery, mergeStatuses(incoming)),
        skip: skipOwnStatus,
    });

    const { dateString: statusDateString, timeString: statusTimeString } = convertFromISOFormat(currentUserStatus?.customUserStatus?.dueDate || '');
    const { dateString: meetingDateString, timeString: meetingTimeString } = convertFromISOFormat(currentUserStatus?.meetingUserStatus?.dateTo || '');

    const expires = statusDateString ? `${statusDateString}, ${statusTimeString}` : statusTimeString;
    const meetingExpires = meetingDateString ? `${meetingDateString}, ${meetingTimeString}` : meetingTimeString;

    const isMobile = useMediaQuery('(max-width: 960px)');

    const [isExpired, setIsExpired] = useState(true);
    const [isMeetingExpired, setIsMeetingExpired] = useState(true);

    const findActiveItem = (currentPath: string, allSections: SectionItems[]) => {
        if (currentPath === '/') {
            return 'home';
        }
        // Profile, phone numbers, groups and the admin pages live under Settings even though their paths differ.
        if (isSettingsPath(currentPath)) {
            const settings = allSections.flatMap(section => section.items).find(item => SETTINGS_RAIL_LINKS.includes(item.link));
            if (settings) return settings;
        }
        for (const section of allSections) {
            for (const item of section.items) {
                if (item.link === currentPath) {
                    return item;
                }
            }
        }

        let bestMatch = null;
        let bestMatchLength = 0;

        for (const section of allSections) {
            for (const item of section.items) {
                if (item.link === "/") continue;

                if (currentPath.startsWith(item.link) && item.link.length > bestMatchLength) {
                    bestMatch = item;
                    bestMatchLength = item.link.length;
                }
            }
        }

        return bestMatch;
    }


    const [isDndOpen, setIsDndOpen] = useState(false);
    const globalDnd = currentUserStatus?.doNotDisturbUserStatus;
    const globalDndDue = parseDue(globalDnd?.dueDate);
    const setGlobalDnd = async (enabled: boolean, dueDate: Date | null) => {
        await toggleDnd({enabled, userId, dueDate: toDndPayload(dueDate)}).unwrap();
        await refetchStatuses();
    };

    useEffect(() => {
        if (getUserStatus(currentUserStatus) === 'TALKING' && callSate === 'IDLE') {
           dispatch(navigateDialer('/error/in-call'));
        } else {
                dispatch(navigateDialer('/'));
        }
    }, [currentUserStatus?.telcoUserStatus?.status]);

    useEffect(() => {
        if (isNavigatingRef.current) return;

        // Check if home path is active
        setIsHomeActive(pathname === '/');
        setIsActiveItem(undefined);

        const activeItem = findActiveItem(pathname, sections);

        if (activeItem && activeItem !== 'home') {
            setIsActiveItem(activeItem);
        }
    }, [pathname, sections]);

    const path = window.location.pathname;
    const isCallsPath = path === '/calls';

    // Mirrors what SidebarSubmenu actually renders. The shell is w-[300px] = 60px rail + 240px
    // submenu, so the rail must stay at 60px whenever a submenu is next to it - otherwise both
    // children ask for the full width and flexbox shrinks them into each other.
    const hasSubmenu = role !== 'SUPERADMIN' && isCallsPath;


    useEffect(() => {
        checkStatusExpiration(currentUserStatus ?? null, setIsExpired);
        checkMeetingStatusExpiration(currentUserStatus ?? null, setIsMeetingExpired);
        const interval = setInterval(() => {
            checkStatusExpiration(currentUserStatus ?? null, setIsExpired);
        }, 5 * 1000);

        return () => clearInterval(interval);
    }, [currentUserStatus?.customUserStatus?.dueDate]);

    useEffect(() => {
        if (user?.email && tenant) {
            Gleap.identify(user.email, {
                name: `${user.firstName} ${user.lastName}`,
                email: user.email,
                companyId: tenant,
            });
        }
    }, [user.email]);

    const getStatusLabel = () => {
        return (currentUserStatus?.customUserStatus?.name) || 'Set status';
    };

    const {keycloak} = useKeycloak();
    const operatorEmail = keycloak.tokenParsed?.email ?? keycloak.tokenParsed?.preferred_username;

    // Dnd-Menu: who you are and your status on top, then status, DND, profile and the rest.
    // Admin panel opens the first admin page; the rest are in the Settings list next to it.
    const openAdminPanel = () => navigate('/admin/report');

    const accountMenu = () => {
        const menuIcon = '!p-0 shrink-0 text-zinc-300';
        if (isSuperadmin) {
            return (
                <PhoneHQDropdown.Item variant="danger" onSelect={() => setIsLogoutOpen(true)}>
                    <LogOut size={16} aria-hidden="true" className="!p-0 shrink-0"/>Log out
                </PhoneHQDropdown.Item>
            );
        }
        const statusActive = currentUserStatus?.customUserStatus?.icon && !isExpired;
        return (
            <>
                <div className="flex items-center gap-2.5 px-2.5 pb-2.5 pt-2">
                    <DsAvatar size={32} name={userFullName} src={user.avatarUrl || undefined}/>
                    <span className="flex min-w-0 flex-col">
                        <span className="truncate text-callout font-semibold text-white">{userFullName}</span>
                        <span className="truncate text-footnote text-zinc-400">{getUserStatusName(currentUserStatus)}</span>
                    </span>
                </div>
                <PhoneHQDropdown.Item onSelect={() => setIsStatusOpen(true)}>
                    {statusActive ? <span className="flex w-4 justify-center">{hexToEmoji(currentUserStatus.customUserStatus.icon)}</span> : <Smile size={16} aria-hidden="true" className={menuIcon}/>}
                    <span className="min-w-0 flex-1 truncate">{statusActive ? currentUserStatus.customUserStatus.name || 'Set status' : 'Set status'}</span>
                    {statusActive && currentUserStatus?.customUserStatus?.dueDate && <span className="text-footnote text-zinc-400">until {expires}</span>}
                </PhoneHQDropdown.Item>
                <PhoneHQDropdown.Item onSelect={() => setIsDndOpen(true)}>
                    <Moon size={16} aria-hidden="true" className={menuIcon}/>
                    <span className="flex-1">Do not disturb</span>
                    <span className="text-footnote text-zinc-400">{dndShortValue(globalDnd)}</span>
                    <ChevronRight size={14} aria-hidden="true" className="!p-0 shrink-0 text-zinc-400"/>
                </PhoneHQDropdown.Item>
                <PhoneHQDropdown.Item onSelect={() => navigate('/profile')}>
                    <UserRound size={16} aria-hidden="true" className={menuIcon}/>Profile
                </PhoneHQDropdown.Item>
                {isEnsEnabled && (
                    <PhoneHQDropdown.Item onSelect={() => navigate('/ens/my-alerts')}>
                        <Bell size={16} aria-hidden="true" className={menuIcon}/>Notifications
                    </PhoneHQDropdown.Item>
                )}
                {role === 'ADMIN' && (
                    <>
                        <PhoneHQDropdown.Separator/>
                        <PhoneHQDropdown.Item onSelect={openAdminPanel}>
                            <Settings size={16} aria-hidden="true" className={menuIcon}/>Admin panel
                        </PhoneHQDropdown.Item>
                    </>
                )}
                <PhoneHQDropdown.Separator/>
                <PhoneHQDropdown.Item variant="danger" onSelect={() => setIsLogoutOpen(true)}>
                    <LogOut size={16} aria-hidden="true" className="!p-0 shrink-0"/>Log out
                </PhoneHQDropdown.Item>
            </>
        );
    };

    const [isStatusOpen, setIsStatusOpen] = useState(false);
    // Settings' Log out button asks the sidebar, which owns the confirmation dialog.
    useEffect(() => {
        const ask = () => setIsLogoutOpen(true);
        window.addEventListener(LOGOUT_REQUEST_EVENT, ask);
        return () => window.removeEventListener(LOGOUT_REQUEST_EVENT, ask);
    }, []);
    const userDropdownTrigger = () => {
        if (isSuperadmin) {
            const who = ['Platform support', operatorEmail, tenant && `Tenant: ${tenant}`].filter(Boolean).join(' · ');
            return (
                <div className='relative mx-auto flex w-fit items-center justify-center rounded-control p-1 hover:bg-nav-hover'>
                    <PhoneHQTooltip content={who} align='start'><DsAvatar size={36} name='Platform Support'/></PhoneHQTooltip>
                </div>
            );
        }
        return (
            <div data-testid="profile-menu" className='relative mx-auto flex w-fit items-center justify-center rounded-control p-1 hover:bg-nav-hover'>
                <PhoneHQTooltip content={getStatusLabel()} align='start'><DsAvatar
                        size={36}
                        name={userFullName}
                        src={user.avatarUrl || undefined}
                        badge={<span
                            data-testid="sidebar-status-dot"
                            className={`block size-3.5 rounded-full border-2 border-solid border-nav-bg ${navStatusDot[getUserStatus(currentUserStatus) as TelcoStatus] ?? 'bg-status-offline'}`}/>}
                    /></PhoneHQTooltip>
            </div>
        )
    }

    const sideBarItem = (item: ChSidebarItem) => {
        const isActive = Boolean(isActiveItem && item.link === isActiveItem.link);

        return (
            <div key={item.link} className="flex justify-center py-1">
                <NavRailItem
                    label={item.label}
                    icon={item.railIcon ?? <PhoneHQGlyph name={item.icon} size={20} className='!text-current'/>}
                    active={isActive}
                    badge={item.labelId === 'chat.menu' ? unreadBadgeCount : undefined}
                    badgeLabel={`${unreadBadgeCount} unread ${unreadBadgeCount === 1 ? 'message' : 'messages'}`}
                    render={({children, ...props}) => <Link to={item.link} {...props}>{children}</Link>}
                />
            </div>
        )
    }

    const getUserCustomStatus = () => {
        return (
            <div className="w-full flex-wrap gap-2 ">
                <div className="text-sm font-medium text-center mb-1 flex">
                    {user.firstName + ' ' + user.lastName}
                    <div className='items-center px-4 flex text-xs'>
                        <div
                            className={clsxMerge(activityStyles({status: getUserStatus(currentUserStatus)}))}></div>
                        <span className='ml-1'>{getUserStatusName(currentUserStatus)} </span>
                    </div>
                </div>

                <div className="flex items-center  gap-2 text-xs w-full break-normal ">
                    <span>{(currentUserStatus?.customUserStatus?.name)}</span>
                </div>
                <div>
                    <div className="text-xs mt-1">
                        {expires ? `Expires : ${expires}` : ''}
                    </div>
                </div>

            </div>
        )
    }

    const getUserMeetingStatus = () => {
        return (
            <div className="w-full flex-wrap gap-2 ">
                <div className="text-sm font-medium text-center mb-1 flex">
                    {user.firstName + ' ' + user.lastName}
                    <div className='items-center px-4 flex text-xs'>
                        <div
                            className={clsxMerge(activityStyles({status: getUserStatus(currentUserStatus)}))}></div>
                        <span className='ml-1'>{getUserStatusName(currentUserStatus)} </span>
                    </div>
                </div>

                <div className="flex items-center  gap-1 text-xs w-full break-normal ">
                    <span>{Boolean(currentUserStatus?.meetingUserStatus.status === 'IN_MEETING'   ) && 'In a meeting'}</span>
                    <div className="text-xs">
                    {meetingExpires ? `until ${meetingExpires}` : ''}
                    </div>
                </div>

            </div>
        )
    }

    const sideBarSection = (section: SectionItems, index: number) => (
        <div key={section.title ?? index}>
            {section.items.map((item: ChSidebarItem) => (sideBarItem(item)))}
        </div>
    )

    // A regular user on an admin URL only sees "Access restricted", without the rail.
    const isRestrictedAdminPath = role === 'USER' && pathname.startsWith('/admin');

    return (
        <>
            {!isRestrictedAdminPath &&
                <div
                    className={`${hasSubmenu ? 'w-[300px] ' : 'w-nav-rail '}` + clsxMerge(sidebarStyles({isMobile}))}>
                    <nav aria-label="Main" className={`flex min-w-0 flex-col justify-between overflow-x-hidden ${hasSubmenu ? 'w-nav-rail flex-none' : 'w-full'}`}>

                        <div className='flex flex-col items-center'>
                            {
                                Boolean(!isMobile && logo) && (
                                    <div className='mb-2 mt-3'>
                                        <NavRailLogo>{logo}</NavRailLogo>
                                    </div>
                                )
                            }

                            <div>
                                {!isSuperadmin && (
                                    <div className='pb-1'>
                                        <div className="flex justify-center py-1">
                                            <NavRailItem
                                                label="Home"
                                                icon={<House size={20} aria-hidden="true" className="!p-0 !text-current"/>}
                                                active={isHomeActive}
                                                render={({children, ...props}) => <Link to={'/'} {...props}>{children}</Link>}
                                            />
                                        </div>
                                    </div>
                                )}
                                {sections.map((section, index) => sideBarSection(section, index))}
                            </div>
                        </div>
                        {user && <div>
                            <div>
                                {Boolean(bottomMenu?.length) && (bottomMenu?.map((item) => (sideBarItem(item))))}
                            </div>
                            <div
                                className={'flex flex-col items-center ' + clsxMerge(sidebarAvatarStyles({isMobile}))}>
                                {currentUserStatus?.meetingUserStatus.status === 'IN_MEETING' && !isMeetingExpired &&
                                    <PhoneHQTooltip content={getUserMeetingStatus()} align='start'>
                                        <div className="mb-1 flex w-full justify-center rounded bg-inverse-surface py-3 hover:cursor-pointer">
                                            <div className="h-5 w-5">
                                                <img alt="" src={googleCalendarIcon}/>
                                            </div>
                                        </div>
                                    </PhoneHQTooltip>
                                }

                                {/* On the rail the profile trigger (an inline button) must centre, not sit left. */}
                                <div className='flex w-full min-w-0 flex-col items-center overflow-hidden'>
                                    {Boolean(currentUserStatus?.customUserStatus.icon) &&
                                        <PhoneHQTooltip content={getUserCustomStatus()} align='start' open={tooltipOpen && !preventTooltipOpen} onOpenChange={(open) => {
                                                if (!preventTooltipOpen) {
                                                    setTooltipOpen(open);
                                                }
                                            }}><div>
                                                <div>
                                                    {Boolean(currentUserStatus?.customUserStatus.icon) && !isExpired &&
                                                        <div
                                                            className="flex bg-inverse-surface justify-center py-3 w-full rounded">
                                                            <div className=" w-5 h-5">
                                                                {hexToEmoji(currentUserStatus?.customUserStatus.icon ?? '', '!w-5 !h-5')}
                                                            </div>
                                                        </div>
                                                    }

                                                </div>
                                            </div></PhoneHQTooltip>
                                    }
                                    <div className='flex w-full justify-center'>
                                        <PhoneHQDropdown
                                            trigger={userDropdownTrigger()}
                                            side='right'
                                            align="end"
                                            contentClassName="w-[260px]"
                                            onOpenChange={(open: boolean) => {
                                                if (open) {
                                                    setTooltipOpen(false);
                                                    SetPreventTooltipOpen(true);
                                                } else {
                                                    setTimeout(() => {
                                                        SetPreventTooltipOpen(false);
                                                    }, 100);
                                                }
                                            }}
                                        >
                                            {accountMenu()}
                                        </PhoneHQDropdown>
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                    </nav>

                    <div className={'hidden'}>
                        <SetStatusDialog open={isStatusOpen} onOpenChange={setIsStatusOpen}
                                         userId={user?.userId} current={currentUserStatus?.customUserStatus}/>

                        <DndDialog
                            open={isDndOpen}
                            onOpenChange={setIsDndOpen}
                            scope={{kind: 'global'}}
                            enabled={isDndOn(globalDnd)}
                            dueDate={globalDndDue}
                            onTurnOn={(dueDate) => setGlobalDnd(true, dueDate)}
                            onTurnOff={() => setGlobalDnd(false, null)}
                        />
                        <LogoutDialog
                            open={isLogoutOpen}
                            onOpenChange={setIsLogoutOpen}
                            onConfirm={async () => {
                                setIsLogoutOpen(false);
                                await performLogout({dispatch, matrixLogout, keycloak});
                            }}
                        />
                    </div>
                    {role !== 'SUPERADMIN' &&
                        <SidebarSubmenu globalDnd={{enabled: isDndOn(globalDnd), dueDate: globalDndDue}}
                                        onTurnOffGlobalDnd={() => setGlobalDnd(false, null)}/>
                    }
                </div>
            }
        </>
    )
}
